import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, catchError, finalize, map, of, throwError } from 'rxjs';

import { AuthService, FeedMessageService, LoadingService, SessionService, SigntrustService, UtilsService } from '@services/index.service';
import { AngularPaths, CheckDomainRequest, Consumer, ErrorCode, MessageType, Response } from '@models/index.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable({providedIn: 'root'})
export class AuthGuard  {
  public constructor(
    private _session: SessionService,
    private _router: Router,
    public _utils: UtilsService,
    private _loading: LoadingService,
    public _auth: AuthService,
    public translate: TranslateService,
    private _signtrustServ: SigntrustService,
    private _notify: FeedMessageService
    ) {}

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // 1) Token vivo -> acceso directo.
    if (this._session.isLoggedIn() && this._session.checkTokenValid()) {
      return true;
    }

    // 2) Token caducado pero con refreshToken -> refresco silencioso ANTES de
    //    entrar (evita el 401 justo al navegar). Solo si el refresh falla se
    //    resuelve como no autenticado.
    if (this._session.hasRefreshToken()) {
      return this._session.refreshSessionShared({ source: 'AuthGuard' }).pipe(
        map((): boolean | UrlTree => true),
        catchError(() => of(this._resolveUnauthenticated(state)))
      );
    }

    // 3) Sin sesión utilizable -> flujo de redirección (SharePoint/Agent/Login).
    return this._resolveUnauthenticated(state);
  }

  /**
   * Resuelve el caso "sin sesión válida": dispara la resolución de dominio SSO
   * para enlaces de SharePoint/Agent (si aplica) y redirige a Login.
   */
  private _resolveUnauthenticated(state: RouterStateSnapshot): UrlTree {
    if (state.url.includes(`/${AngularPaths.SIGNTRUST}/`)) {
      const cleanData = state.url.replace(`/${AngularPaths.SIGNTRUST}/`, '');
      localStorage.setItem('spRedirectId', cleanData);
      this._signtrustServ.getRemoteDocument(cleanData).subscribe(
        (res: Response<any>) => {
          if (res.data) {
            const spDomainConfig = this._utils.parseUserPreferenceConfig(res.data.toString());
            if (spDomainConfig.domain) {
              this._loading.showGlobalLoading();
              const checkDomainRequest: CheckDomainRequest = {
                username: spDomainConfig.domain,
                consumer: Consumer.PERSONAL_AREA,
                domainToReturn: window.location.origin,
              };
              this._auth.checkDomain(checkDomainRequest)
                .pipe(
                  catchError(err => throwError(() => err)),
                  finalize(() => {})
                )
                .subscribe((response: Response<any>) => {
                  if (response.errorCode === ErrorCode.REDIRECT) {
                    if (!response.data.url) {
                      this._notify.feedBackResult(MessageType.ERROR, this.translate.instant('GLOBAL.ERROR-RETRY'));
                      this._loading.hideGlobalLoading();
                    } else {
                      this._loading.hideGlobalLoading();
                      window.location.href = response.data.url;
                    }
                  } else {
                    this._loading.hideGlobalLoading();
                  }
                });
            }
          }
        },
        error => {
          this._loading.hideGlobalLoading();
          console.error(error);
        });
    }

    if (state.url.includes(`/${AngularPaths.SIGN_BY_AGENT}/`)) {
      const cleanData = state.url.replace(`/${AngularPaths.SIGN_BY_AGENT}/`, '');
      localStorage.setItem('agentRedirectId', cleanData);
      this._signtrustServ.getRemoteDocument(cleanData).subscribe(
        (res: Response<any>) => {
          if (res.data) {
            const agentConfig = this._utils.parseUserPreferenceConfig(res.data.toString());
            if (agentConfig.loginToken) {
              this._loading.showGlobalLoading();
              this._router.navigate([`${AngularPaths.LOGIN_BY_TOKEN}/${agentConfig.loginToken}`], { replaceUrl: true });
            }
          }
        },
        error => {
          this._loading.hideGlobalLoading();
          console.error(error);
        });
    }

    return this._router.parseUrl(AngularPaths.LOGIN);
  }
}
